import React, { useId, useState } from 'react'
import { StyleProp, StyleSheet, Text, TextInput, TextInputProps, View, ViewStyle } from 'react-native'

import { tokens } from '../../theme/tokens'

interface ControlledTextInputBaseProps {
  value: string
  onChangeText: (text: string) => void
  onBlur?: TextInputProps['onBlur']
  autoFocus?: boolean
  label?: string
  /** Rendered after the label, e.g. a translated "(optional)". */
  optionalLabel?: string
  placeholder?: string
  errorMessage?: string
  /** Overrides the invalid styling; defaults to `Boolean(errorMessage)`. */
  isInvalid?: boolean
  hintText?: string
  /** Rendered before the hint text (e.g. an info icon). */
  hintSlot?: React.ReactNode
  isDisabled?: boolean
  isReadOnly?: boolean
  maxLength?: number
  /** Renders the live character counter; requires maxLength. */
  showCharCount?: boolean
  leftSlot?: React.ReactNode
  testID?: string
  /** Part testIDs so app test contracts (e.g. `${name}-label`) survive extraction. */
  labelTestID?: string
  optionalLabelTestID?: string
  hintTestID?: string
  errorTestID?: string
  charCountTestID?: string
  keyboardType?: TextInputProps['keyboardType']
  /**
   * Cross-platform autofill hint (e.g. 'postal-code', 'tel', 'off').
   * On iOS `textContentType` takes precedence when both are set — pass one,
   * not both, unless the platforms genuinely need different values.
   */
  autoComplete?: TextInputProps['autoComplete']
  /** iOS-only autofill hint; see `autoComplete` for precedence. */
  textContentType?: TextInputProps['textContentType']
  /** Announced field name; defaults to `label`. */
  accessibilityLabel?: string
  /**
   * 'redacted' marks the field so session recorders (Clarity) mask its
   * contents. Set on fields carrying secrets or PII (access codes, phone).
   */
  privacy?: 'default' | 'redacted'
  style?: StyleProp<ViewStyle>
}

// secureTextEntry does not work with multiline={true} (RN TextInput docs);
// the discriminated union makes the combination unrepresentable.
export type ControlledTextInputProps = ControlledTextInputBaseProps &
  (
    | { multiline?: false; secureTextEntry?: boolean; numberOfLines?: never }
    | { multiline: true; secureTextEntry?: never; numberOfLines?: number }
  )

/**
 * ControlledTextInput — the shared form-input primitive (CBP-3943).
 *
 * Form-library-agnostic by design: takes `value`/`onChangeText` directly, so
 * each app binds it to Formik (or any successor) with a thin app-side
 * adapter. Masking, debounce, and validation schemas stay in the consuming
 * app — see `ControlledPhoneInput`/`ControlledZipCodeInput` for the shared
 * compositions.
 */
export const ControlledTextInput = ({
  value,
  onChangeText,
  onBlur,
  autoFocus,
  label,
  optionalLabel,
  placeholder,
  errorMessage,
  isInvalid: isInvalidProp,
  hintText,
  hintSlot,
  isDisabled = false,
  isReadOnly = false,
  secureTextEntry = false,
  multiline = false,
  numberOfLines,
  maxLength,
  showCharCount = false,
  leftSlot,
  testID,
  labelTestID,
  optionalLabelTestID,
  hintTestID,
  errorTestID,
  charCountTestID,
  keyboardType,
  autoComplete,
  textContentType,
  accessibilityLabel,
  privacy = 'default',
  style,
}: ControlledTextInputProps) => {
  const autoId = useId()
  const isInvalid = isInvalidProp ?? Boolean(errorMessage)
  // Reference the error element only when it will actually render, else a
  // screen reader is pointed at a nonexistent id (and keep the hint visible
  // when the error text is suppressed, e.g. hideErrorMessage bindings).
  const hasErrorText = isInvalid && Boolean(errorMessage)
  const hintId = `${autoId}-hint`
  const errorId = `${autoId}-error`
  const describedBy = hasErrorText ? errorId : hintText ? hintId : undefined
  // react-native-web turns dataSet into data-* attributes; Clarity masks
  // elements carrying data-clarity-mask. Inert on native. dataSet is a
  // react-native-web extension absent from RN core types, hence the cast.
  const redactionProps = (privacy === 'redacted' ? { dataSet: { clarityMask: 'true' } } : {}) as TextInputProps
  const [isFocused, setIsFocused] = useState(false)

  return (
    <View style={[styles.container, style]}>
      {label ? (
        <Text nativeID={`${autoId}-label`} testID={labelTestID} style={styles.label}>
          {label}
          {optionalLabel ? (
            <Text testID={optionalLabelTestID} style={styles.optionalLabel}>
              {' '}
              {optionalLabel}
            </Text>
          ) : null}
        </Text>
      ) : null}

      <View
        style={[
          styles.inputRow,
          isFocused && styles.inputRowFocused,
          isInvalid && styles.inputRowError,
          isDisabled && styles.inputRowDisabled,
        ]}
      >
        {leftSlot ? <View style={styles.leftSlot}>{leftSlot}</View> : null}
        <TextInput
          style={[styles.input, multiline && styles.inputMultiline]}
          value={value}
          onChangeText={onChangeText}
          onFocus={() => setIsFocused(true)}
          onBlur={event => {
            setIsFocused(false)
            onBlur?.(event)
          }}
          autoFocus={autoFocus}
          placeholder={placeholder}
          placeholderTextColor={tokens.colors.brand.textPlaceholder}
          secureTextEntry={multiline ? false : secureTextEntry}
          multiline={multiline}
          numberOfLines={multiline ? numberOfLines : undefined}
          maxLength={maxLength}
          editable={!isDisabled && !isReadOnly}
          testID={testID}
          keyboardType={keyboardType}
          autoComplete={autoComplete}
          textContentType={textContentType}
          accessibilityLabel={accessibilityLabel ?? label}
          aria-invalid={isInvalid}
          aria-disabled={isDisabled || undefined}
          aria-describedby={describedBy}
          {...redactionProps}
        />
      </View>

      <View style={styles.footer}>
        {hintText && !hasErrorText ? (
          <View style={styles.hintRow}>
            {hintSlot}
            <Text nativeID={hintId} testID={hintTestID} style={[styles.hint, hintSlot != null && styles.hintWithSlot]}>
              {hintText}
            </Text>
          </View>
        ) : null}
        {hasErrorText ? (
          <Text nativeID={errorId} testID={errorTestID} accessibilityRole="alert" style={styles.error}>
            {errorMessage}
          </Text>
        ) : null}
        {maxLength && showCharCount ? (
          <Text testID={charCountTestID} style={[styles.charCount, isInvalid && styles.charCountError]}>
            {value.length}/{maxLength}
          </Text>
        ) : null}
      </View>
    </View>
  )
}

ControlledTextInput.displayName = 'ControlledTextInput'

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: tokens.colors.brand.textSoftBlack,
    marginBottom: 4,
  },
  optionalLabel: {
    fontWeight: '400',
    color: tokens.colors.brand.secondaryGrey,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: tokens.colors.brand.textSoftBlack,
    borderRadius: 8,
    backgroundColor: tokens.colors.brand.white,
    paddingHorizontal: 12,
    minHeight: 44,
  },
  inputRowFocused: {
    borderColor: tokens.colors.brand.black,
  },
  inputRowError: {
    borderColor: tokens.colors.brand.vermillion,
  },
  inputRowDisabled: {
    backgroundColor: tokens.colors.brand.lightGrey,
  },
  leftSlot: {
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: tokens.colors.brand.textSoftBlack,
    paddingVertical: 10,
  },
  inputMultiline: {
    minHeight: 88,
    textAlignVertical: 'top',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: 2,
  },
  hintRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  hint: {
    flex: 1,
    fontSize: 12,
    color: tokens.colors.brand.secondaryGrey,
  },
  hintWithSlot: {
    paddingLeft: 20,
  },
  error: {
    flex: 1,
    fontSize: 12,
    color: tokens.colors.brand.vermillion,
  },
  charCount: {
    fontSize: 12,
    color: tokens.colors.brand.secondaryGrey,
    marginLeft: 8,
  },
  charCountError: {
    color: tokens.colors.brand.vermillion,
  },
})
